import { postRequest } from "../services/apiService";

/**
 * Remembers which campaign brought a visitor, so a signup or a Stripe payment
 * can be traced back to it.
 *
 *  - First touch is captured on the first pageview this browser makes and is
 *    never overwritten. It answers where the account came from.
 *  - Last touch is refreshed by any visit that carries campaign params. It
 *    answers which click gets checkout credit.
 *
 * Both are plain localStorage. Without storage the getters return null and
 * callers carry on without attribution. index.tsx only starts this for
 * visitors who have not opted out of analytics.
 */

const FIRST_TOUCH_KEY = "bb.attribution.firstTouch";
const LAST_TOUCH_KEY = "bb.attribution.lastTouch";
const SYNCED_KEY = "bb.attribution.synced";

const MAX_VALUE_LENGTH = 450;

const CAMPAIGN_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
  "fbclid",
  "msclkid",
  "ttclid",
] as const;

export type Attribution = Record<string, string>;

export type AttributionPayload = {
  first_touch?: Attribution;
  last_touch?: Attribution;
};

const readCampaignParams = (): Attribution => {
  const params = new URLSearchParams(window.location.search);
  const out: Attribution = {};
  CAMPAIGN_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) out[key] = value.slice(0, MAX_VALUE_LENGTH);
  });
  return out;
};

/** Origin and path only: a referrer's query string can hold another site's tokens. */
const readReferrer = (): string | undefined => {
  if (!document.referrer) return undefined;
  try {
    const url = new URL(document.referrer);
    return `${url.origin}${url.pathname}`.slice(0, MAX_VALUE_LENGTH);
  } catch {
    return undefined;
  }
};

const buildTouch = (campaign: Attribution): Attribution => {
  const touch: Attribution = {
    ...campaign,
    landing_path: window.location.pathname.slice(0, MAX_VALUE_LENGTH),
    first_seen_at: new Date().toISOString(),
  };
  const referrer = readReferrer();
  if (referrer) touch.referrer = referrer;
  return touch;
};

const safeGet = (key: string): Attribution | null => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object"
      ? (parsed as Attribution)
      : null;
  } catch {
    return null;
  }
};

const safeSet = (key: string, value: Attribution) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* attribution is best effort */
  }
};

/** Call once per load, before the SPA router can touch the URL. Idempotent. */
export const initAttribution = () => {
  if (typeof window === "undefined") return;
  const campaign = readCampaignParams();
  if (!safeGet(FIRST_TOUCH_KEY)) safeSet(FIRST_TOUCH_KEY, buildTouch(campaign));
  if (Object.keys(campaign).length > 0) {
    safeSet(LAST_TOUCH_KEY, buildTouch(campaign));
  }
};

/** Both touches, in the shape the API expects. Null when nothing was captured. */
export const getAttributionPayload = (): AttributionPayload | null => {
  const firstTouch = safeGet(FIRST_TOUCH_KEY);
  const lastTouch = safeGet(LAST_TOUCH_KEY);
  if (!firstTouch && !lastTouch) return null;
  return {
    ...(firstTouch ? { first_touch: firstTouch } : {}),
    ...(lastTouch ? { last_touch: lastTouch } : {}),
  };
};

/**
 * Saves the touches on the signed-in account. Call only with a session. The
 * synced marker keeps this to one request per account and change of
 * attribution, so a new campaign click is sent again and a plain revisit is not.
 */
export const syncAttributionToAccount = async (
  userId: string | number,
): Promise<void> => {
  const payload = getAttributionPayload();
  if (!payload) return;
  const marker = `${userId}:${JSON.stringify(payload)}`;
  try {
    if (localStorage.getItem(SYNCED_KEY) === marker) return;
  } catch {
    /* the worst case is a repeat POST */
  }
  try {
    await postRequest("/api/v1/users/attribution", payload);
    try {
      localStorage.setItem(SYNCED_KEY, marker);
    } catch {
      /* the worst case is a repeat POST */
    }
  } catch {
    /* retried on the next signed-in load */
  }
};
